import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { useTranslation } from 'react-i18next';
import { useMailOrbitEnabled } from 'src/utils/mailorbitFeature';
import {
  claimConversation,
  releaseConversation,
  setConversationStatus,
  reopenConversation,
  getStatuses,
  getMailOrbitErrorKey,
  isMailOrbitError,
  isMailOrbitMock
} from 'src/utils/MailOrbitService';

const REASON_MAX = 500;

// Bu status'lar claim/release/pipeline tarafından yönetilir (status-mapping.md §3); agent seçicisinde yok.
const SYSTEM_OWNED_KEYS = ['New', 'Queued', 'Assigned'];

/**
 * Conversation iş aksiyonları: claim, release, status değiştirme, reopen (agent kapsamı).
 * Kapı kapalıyken hiçbir şey çizmez ve istek atmaz.
 *
 * - detail: GET conversation yanıtı
 * - onPatch(partial): görünümdeki detail'e iyimser (optimistic) yama uygular
 * - onRefresh(): conversation'ı sunucudan yeniden okur
 * - allowTerminalStatuses: yalnızca bağlı AÇIK iş emri YOKSA true verilir (SR-031). İş emrini yalnızca
 *   Sirius kapatır; bilgi yoksa/yüklenmediyse varsayılan (false) terminal durumları sunmaz.
 */
const WorkActions = ({ conversationId, detail, onPatch, onRefresh, allowTerminalStatuses }) => {
  const enabled = useMailOrbitEnabled();
  const { t } = useTranslation(['mailorbitActions', 'mailorbit']);
  const [statuses, setStatuses] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null); // { type: 'status'|'reopen', status? }
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);

  const canWork = !!detail && detail.canWork === true;
  const terminal = !!(detail && detail.statusIsTerminal);

  useEffect(() => {
    if (!enabled || !canWork) return undefined;
    let cancelled = false;
    getStatuses()
      .then(list => {
        if (!cancelled) setStatuses(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [enabled, canWork]);

  if (!enabled || !detail || !canWork) return null;

  const showError = (e, conflictKey) => {
    if (isMailOrbitError(e) && e.kind === 'conflict') {
      setError(t(`mailorbitActions:${conflictKey}`));
    } else {
      setError(t(`mailorbit:${getMailOrbitErrorKey(e)}`));
    }
  };

  const refresh = async () => {
    // Mock fixture'ı durağandır: yenileme iyimser değişikliği geri alırdı.
    if (isMailOrbitMock()) return;
    try {
      await onRefresh();
    } catch (e) {
      // yenileme başarısızsa mevcut görünüm kalır
    }
  };

  // action: sunucu çağrısı; patch/revert: iyimser güncelleme ve geri alma
  const run = async ({ action, patch, revert, conflictKey }) => {
    setBusy(true);
    setError(null);
    if (patch) onPatch(patch);
    try {
      await action();
      await refresh();
    } catch (e) {
      if (revert) onPatch(revert);
      showError(e, conflictKey);
      if (isMailOrbitError(e) && (e.kind === 'conflict' || e.kind === 'conversationClosed')) {
        await refresh();
      }
    } finally {
      setBusy(false);
    }
  };

  const onClaim = () =>
    run({
      action: () => claimConversation(conversationId),
      // Gerçek modda sahip kimliği yenilemeyle gelir; mock'ta yenileme yok, görünür kalsın.
      patch: isMailOrbitMock() ? { currentOwnerUserId: 'mock-self' } : null,
      revert: { currentOwnerUserId: detail.currentOwnerUserId },
      conflictKey: 'claim_conflict'
    });

  const onRelease = () =>
    run({
      action: () => releaseConversation(conversationId),
      patch: { currentOwnerUserId: null },
      revert: { currentOwnerUserId: detail.currentOwnerUserId },
      conflictKey: 'release_conflict'
    });

  const closeDialog = () => {
    setDialog(null);
    setReason('');
    setReasonError(false);
  };

  const onConfirm = async () => {
    const text = reason.trim();
    if (dialog.type === 'reopen') {
      if (!text) {
        setReasonError(true);
        return;
      }
      closeDialog();
      await run({
        action: () => reopenConversation(conversationId, text),
        patch: isMailOrbitMock() ? { statusIsTerminal: false } : null,
        revert: { statusIsTerminal: detail.statusIsTerminal },
        conflictKey: 'status_conflict'
      });
      return;
    }
    const st = dialog.status;
    closeDialog();
    await run({
      action: () => setConversationStatus(conversationId, st.id, text || undefined),
      patch: { statusKey: st.key, statusName: st.name, statusIsTerminal: !!st.isTerminal },
      revert: {
        statusKey: detail.statusKey,
        statusName: detail.statusName,
        statusIsTerminal: detail.statusIsTerminal
      },
      conflictKey: 'status_conflict'
    });
  };

  const options = statuses.filter(
    s =>
      !SYSTEM_OWNED_KEYS.includes(s.key) &&
      s.key !== detail.statusKey
  );
  // Bağlı açık iş emri varken terminal durumlar seçilemez (SR-031); sebebiyle birlikte gösterilir.
  const isLocked = s => !allowTerminalStatuses && !!s.isTerminal;
  const pickStatus = st => {
    setMenuAnchor(null);
    // Status değişiminde sebep isteğe bağlıdır; yalnızca durum tanımı zorunlu kılıyorsa sorulur.
    if (st.requiresReason === true) {
      setDialog({ type: 'status', status: st });
      return;
    }
    run({
      action: () => setConversationStatus(conversationId, st.id),
      patch: { statusKey: st.key, statusName: st.name, statusIsTerminal: !!st.isTerminal },
      revert: {
        statusKey: detail.statusKey,
        statusName: detail.statusName,
        statusIsTerminal: detail.statusIsTerminal
      },
      conflictKey: 'status_conflict'
    });
  };
  const owned = !!detail.currentOwnerUserId;
  // isOwnedByMe gelmezse (eski sürüm) sahipli her konuşmada Bırak gösterilir.
  const canRelease = owned && detail.isOwnedByMe !== false;

  return (
    <Box>
      <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {!terminal && !owned && (
          <Button size="small" variant="contained" color="primary" disabled={busy} onClick={onClaim}>
            {t('mailorbitActions:claim')}
          </Button>
        )}
        {!terminal && canRelease && (
          <Button size="small" variant="outlined" disabled={busy} onClick={onRelease}>
            {t('mailorbitActions:release')}
          </Button>
        )}
        {!terminal && options.length > 0 && (
          <>
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              aria-haspopup="true"
              aria-expanded={!!menuAnchor}
              endIcon={<ArrowDropDownIcon />}
              onClick={e => setMenuAnchor(e.currentTarget)}
            >
              {t('mailorbitActions:status_menu', { status: detail.statusName || detail.statusKey || '-' })}
            </Button>
            <Menu
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={() => setMenuAnchor(null)}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              {options.map(s => (
                <MenuItem key={s.id} disabled={isLocked(s)} onClick={() => pickStatus(s)}>
                  {s.name}
                  {isLocked(s) && (
                    <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                      {t('mailorbitActions:closes_with_work_order')}
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
        {!terminal && !allowTerminalStatuses && (
          <Typography variant="caption" color="textSecondary" style={{ flexBasis: '100%' }}>
            {t('mailorbitActions:linked_work_order_close_hint')}
          </Typography>
        )}
        {terminal && (
          <>
            <Typography variant="caption" color="textSecondary">
              {t('mailorbitActions:closed_hint')}
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={busy}
              onClick={() => setDialog({ type: 'reopen' })}
            >
              {t('mailorbitActions:reopen')}
            </Button>
          </>
        )}
      </Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} style={{ marginTop: 8 }}>
          {error}
        </Alert>
      )}
      <Dialog open={!!dialog} onClose={closeDialog} fullWidth maxWidth="xs">
        {dialog && (
          <>
            <DialogTitle>
              {dialog.type === 'reopen'
                ? t('mailorbitActions:reopen_dialog_title')
                : t('mailorbitActions:status_dialog_title', {
                    status: dialog.status && dialog.status.name
                  })}
            </DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                label={
                  dialog.type === 'reopen'
                    ? t('mailorbitActions:reason')
                    : t('mailorbitActions:reason_optional')
                }
                value={reason}
                onChange={e => {
                  setReason(e.target.value);
                  setReasonError(false);
                }}
                inputProps={{ maxLength: REASON_MAX }}
                error={reasonError}
                helperText={
                  reasonError
                    ? t('mailorbitActions:reason_required')
                    : t('mailorbitActions:reason_limit', { count: reason.length, max: REASON_MAX })
                }
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog}>{t('mailorbitActions:cancel')}</Button>
              <Button color="primary" variant="contained" onClick={onConfirm}>
                {t('mailorbitActions:confirm')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

WorkActions.propTypes = {
  conversationId: PropTypes.string.isRequired,
  detail: PropTypes.object,
  onPatch: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  allowTerminalStatuses: PropTypes.bool
};

WorkActions.defaultProps = { allowTerminalStatuses: false };

export default WorkActions;
